import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Routes } from '@angular/router';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { LoginComponent } from './modules/auth/login/login.component';
import { AuthGuard } from 'src/app/services/auth-guard.service';
import { UnauthorizedComponent } from './pages/portal/unauthorized/unauthorized.component';
import { DashboardComponent } from './pages/portal/landing/dashboard/dashboard.component';
import { CorpDashboardComponent } from './pages/portal/corp/corp-dashboard/corp-dashboard.component';
import { HierarchyElementDashboardComponent } from './pages/portal/hierarchy-element/hierarchy-element-dashboard/hierarchy-element-dashboard.component';
import { BotAnalyticsComponent } from './pages/portal/bot/bot-analytics/bot-analytics.component';
import { CorpsAdminComponent } from './pages/admin/corp-admin/corps-admin/corps-admin.component';
import { BasicLayoutComponent } from './layouts/basic-layout/basic-layout.component';
import { AdminGuard } from './services/admin-guard.service';
import { PermissionGuard } from './services/permission-guard.service';
import { BotAdminComponent } from './pages/admin/bot-admin/bot-admin.component';
import { ProfileComponent } from './pages/portal/profile/profile.component';
import { BotSettingsComponent } from './pages/portal/bot/bot-settings/bot-settings.component';
import { CorpSettingsComponent } from './pages/portal/corp/corp-settings/corp-settings.component';
import { HeirarchyElementSettingsComponent } from './pages/portal/hierarchy-element/hierarchy-element-settings/hierarchy-element-settings.component';
import { CorpSettingsAdminComponent } from './pages/admin/corp-admin/corp-settings-admin/corp-settings-admin.component';
import { AdminProfileComponent } from './pages/admin/admin-profile/admin-profile.component';
import { UsersAdminComponent } from './pages/admin/users-admin/users-admin/users-admin.component';
import { Permissions } from './utils/permissions/permissions';
import { RolesAdminComponent } from './pages/admin/roles-admin/roles-admin.component';
import { CorpHierarchiesAdminComponent } from './pages/admin/corp-admin/corp-hierarchies-admin/corp-hierarchies-admin.component';
import { HierarchyElementsAdminComponent } from './pages/admin/hierarchies-admin/hierarchy-elements-admin/hierarchy-elements-admin.component';
import { HierarchyElementBotsAdminComponent } from './pages/admin/hierarchies-admin/hierarchy-element-bots-admin/hierarchy-element-bots-admin.component';
import { BotExportComponent } from './pages/portal/bot/bot-export/bot-export.component';
import { UserNotificationsComponent } from './components/notifications/user-notifications/user-notifications.component';
import { HlContainerComponent } from './components/_human-in-the-loop/hl-container/hl-container.component';
import { CreateCampaignComponent } from './components/multi-list/create-campaign/create-campaign.component';
import { BotKnowledgeBankComponent } from './pages/portal/bot/bot-knowledge-bank/bot-knowledge-bank.component';
import { PriorityDashboardPage } from './pages/portal/voice-ai/pages/priority-dashboard/priority-dashboard.page';
import { BotConfigCoreRouterOutletComponent } from './modules/bot-config-core';
import { LogoutComponent } from './modules/auth/logout/logout.component';
import { ChangePasswordComponent } from './modules/auth/change-password/change-password.component';

const routes: Routes = [
  { path: 'login', component: LoginComponent },
  { path: 'logout', component: LogoutComponent },
  // Public on purpose: reached mid-login, before the user has an app session.
  { path: 'change-password', component: ChangePasswordComponent },
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [AdminGuard],
    children: [
      {
        path: '',
        redirectTo: 'corps',
        pathMatch: 'full',
      },
      {
        path: 'corps',
        component: CorpsAdminComponent,
      },
      {
        path: 'roles',
        component: RolesAdminComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.CAN_READ_ROLES },
      },
      {
        path: 'users',
        component: UsersAdminComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.CAN_READ_USERS },
      },
      {
        path: 'corps/:corp',
        redirectTo: 'corps/:corp/settings',
        pathMatch: 'full',
      },
      {
        path: 'corps/:corp/settings',
        component: CorpSettingsAdminComponent,
      },
      {
        path: 'corps/:corp/hierarchies',
        component: CorpHierarchiesAdminComponent,
      },
      {
        path: 'corps/:corp/hierarchies/:hierarchy',
        component: HierarchyElementsAdminComponent,
      },
      {
        path: 'corps/:corp/hierarchies/:hierarchy/hierarchy-el/:hierarchyElementSystemName',
        component: HierarchyElementsAdminComponent,
      },
      {
        path: 'corps/:corp/hierarchies/:hierarchy/hierarchy-el/:hierarchyElementSystemName/bots',
        component: HierarchyElementBotsAdminComponent,
      },
      {
        path: 'corps/:corp/hierarchies/:hierarchy/hierarchy-el/:hierarchyElementSystemName/bot/:bot',
        component: BotAdminComponent,
      },
      {
        path: 'profile',
        component: AdminProfileComponent,
      },
      {
        path: '**',
        redirectTo: 'corps',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'portal',
    component: BasicLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'unauthorized',
        component: UnauthorizedComponent,
      },
      {
        path: 'profile',
        component: ProfileComponent,
      },
      {
        path: 'notifications',
        component: UserNotificationsComponent,
      },
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [PermissionGuard],
      },
      {
        path: 'corps/:corp/dashboard',
        component: CorpDashboardComponent,
        canActivate: [PermissionGuard],
      },
      {
        path: 'corps/:corp/settings',
        component: CorpSettingsComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.CAN_READ_CORP_SETTINGS },
      },
      {
        path: 'corps/:corp/human-in-the-loop',
        redirectTo: 'corps/:corp/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'corps/:corp/human-in-the-loop/:conversationId',
        component: HlContainerComponent,
        canActivate: [PermissionGuard],
      },
      {
        path: 'corps/:corp/hierarchy-el/:hierarchyElementSystemName/dashboard',
        component: HierarchyElementDashboardComponent,
        canActivate: [PermissionGuard],
      },
      {
        path: 'corps/:corp/hierarchy-el/:hierarchyElementSystemName/human-in-the-loop',
        redirectTo: 'corps/:corp/hierarchy-el/:hierarchyElementSystemName/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'corps/:corp/hierarchy-el/:hierarchyElementSystemName/human-in-the-loop/:conversationId',
        component: HlContainerComponent,
        canActivate: [PermissionGuard],
      },
      {
        path: 'corps/:corp/hierarchy-el/:hierarchyElementSystemName/settings',
        component: HeirarchyElementSettingsComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.CAN_READ_HIERARCHY_SETTINGS },
      },
      {
        path: 'corps/:corp/hierarchy-el/:hierarchyElementSystemName/bots/:bot',
        component: BotConfigCoreRouterOutletComponent,
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            component: BotAnalyticsComponent,
            canActivate: [PermissionGuard],
            data: {
              permission: Permissions.CAN_READ_BOT_ANALYTICS,
              botBreadcrumbTitle: 'Dashboard',
            },
          },
          {
            path: 'settings',
            component: BotSettingsComponent,
            canActivate: [PermissionGuard],
            data: {
              permission: Permissions.CAN_READ_BOT_SETTINGS,
              botBreadcrumbTitle: 'Settings & Test',
            },
          },
          {
            path: 'human-in-the-loop',
            component: HlContainerComponent,
            canActivate: [PermissionGuard],
            data: { permissions: [Permissions.CAN_HANDLE_BOT_SUPPORT, Permissions.CAN_ADMIN_BOT_HIL] },
          },
          {
            path: 'knowledge-bank',
            component: BotKnowledgeBankComponent,
            canActivate: [PermissionGuard],
            data: {
              permission: Permissions.CAN_ACCESS_KNOWLEDGE_BANK,
              botBreadcrumbTitle: 'Knowledge Bank',
            },
          },
          {
            path: 'voice-ai-dashboard',
            component: PriorityDashboardPage,
            data: { botBreadcrumbTitle: 'Voice AI' },
          },
          {
            path: 'voice-ai-conversation',
            loadChildren: () => import('./modules/voice-ai-conversation/voice-ai-conversation.module').then(m => m.VoiceAiConversationModule),
            canActivate: [PermissionGuard],
            data: {
              botBreadcrumbTitle: 'Conversation',
            },
          },
          {
            path: 'dashboard/create-new-campaign',
            component: CreateCampaignComponent,
          },
          {
            path: 'export',
            component: BotExportComponent,
            canActivate: [PermissionGuard],
            data: {
              permission: Permissions.CAN_EXPORT_BOT,
              botBreadcrumbTitle: 'Settings & Test',
            },
          },
          {
            path: 'human-in-the-loop/:conversationId',
            component: HlContainerComponent,
            canActivate: [PermissionGuard],
            data: { permissions: [Permissions.CAN_HANDLE_BOT_SUPPORT, Permissions.CAN_ADMIN_BOT_HIL] },
          },
          {
            path: 'human-in-the-loop/:conversationId/:userId',
            component: HlContainerComponent,
            canActivate: [PermissionGuard],
            data: { permissions: [Permissions.CAN_HANDLE_BOT_SUPPORT, Permissions.CAN_ADMIN_BOT_HIL] },
          },
        ],
      },
      {
        path: 'corps/:corp/hierarchy-el/:hierarchyElementSystemName',
        redirectTo: 'corps/:corp/hierarchy-el/:hierarchyElementSystemName/dashboard',
        pathMatch: 'prefix',
      },
      {
        path: 'corps/:corp',
        redirectTo: 'corps/:corp/dashboard',
        pathMatch: 'prefix',
      },
      {
        path: '**',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
  { path: '**', redirectTo: 'portal', pathMatch: 'full' },
];

@NgModule({
  imports: [
    CommonModule,
    BrowserModule,
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      onSameUrlNavigation: 'reload',
      enableTracing: false,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
